import React, { useEffect, useState, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { Button } from "bootstrap-4-react/lib/components";
import Table from "react-bootstrap/Table";
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import BatchPagination from '../batch/BatchPagination';
import { featureModule, FEATURE_CARRIERCONNECTMANAGEMENT, numOfPage, permssionCreate, permssionEdit, permssionView, DEFAULT_CARRIER_CONNECT_FILTERS } from '../../constant';
import LoaderSpinner from '../common/Loader';
import ActionItems from '../common/showActions';
import _ from 'lodash';
import { checkPermission, getStatusClass, renderTableRow } from '../../config/utils';
import { deleteCarrierConnectRecords, getCarrierConnectListing } from '../../store/actions/carrierConnect.action';

import { carrierConnectBackDetails } from '../../store/actions/employee';
import moment from 'moment';
import './CarrierConnect.css';
import SearchCarrierConnect from './SearchCarrierConnect';
import CarrierConnectFilter from './CarrierConnectFilter';
import AlertDialogModal from '../common/AlertDialog';
import { toast } from 'react-toastify';
/* istanbul ignore next */
const CarrierConnectComponent = (props) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    // Memoize dataLoad to prevent unnecessary re-renders using shared constant
    const dataLoad = useMemo(() => ({ ...DEFAULT_CARRIER_CONNECT_FILTERS }), []);

    // Separate pagination state from filter state to avoid unnecessary API calls
    const [paginationData, setPaginationData] = useState({
        totalRecords: 0,
        totalPages: 1
    });
    const selectAllRef = useRef();
    const [filterData, setfilterData] = useState(dataLoad);
    const [carrierConnectList, setCarrierConnectList] = useState([]);
    const userDetails = useSelector((state) => state.auth.user);
    const { carrierConnectData, carrierConnectStatus } = useSelector((state) => state.carrierConnect);

    // Helper function to check if user has warehouse role (cannot create new carrier connects)
    const isWarehouseRole = () => {
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        return roleName === 'warehouse manager' || roleName === 'warehouse worker';
    };

    // Determine if the current user is allowed to delete a specific carrier connect item
    const canDeleteItem = (item) => {
        // Warehouse Manager / Warehouse Worker: no delete at all
        if (isWarehouseRole()) return false;

        // Super Admin: can delete only items that have not been charged yet
        if (userDetails?.Value?.isSuperAdmin) {
            const billing = (item.billingStatus || '').toString().trim().toLowerCase();
            return billing === '' || billing === 'unbilled';
        }

        // Seller / Admin: can delete only items with status "New"
        const status = (item.statusName || '').toString().trim().toLowerCase();
        return status === 'new';
    };

    // Whether the current role is allowed to see any delete UI at all
    const canSeeDeleteUI = !isWarehouseRole();
    const { carrierConnectPrevState } = useSelector((state) => state.employee);
    
    const [isInitialLoad, setIsInitialLoad] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
    const [hasRestoredFromPrevState, setHasRestoredFromPrevState] = useState(false);

    console.log('isApiCallInProgress:', isApiCallInProgress);
    
    // Use ref to track restoration immediately to prevent race conditions
    const isRestoringFromPrevState = useRef(false);
    
    // State to track the last searched keyword for displaying search result text
    const [lastSearchedKeyword, setLastSearchedKeyword] = useState('');
    
    // State to control when search should be triggered
    const [shouldTriggerSearch, setShouldTriggerSearch] = useState(false);

    // Map API response structure: Based on test data, prioritize CarrierConnections and EntityList
    useEffect(() => {
        // Clone items from Redux state into local component state to avoid
        // accidental mutations of the store objects. Also ensure isChecked is
        // a boolean so checkbox bindings are stable.
        const list = carrierConnectData?.EntityList || [];
        if (Array.isArray(list) && list.length > 0) {
            const cloned = list.map(item => ({ ...item, isChecked: Boolean(item.isChecked) }));
            setCarrierConnectList(cloned);
        } else {
            setCarrierConnectList([]);
        }
    }, [carrierConnectData]);

    useEffect(() => {
        if (selectAllRef.current) {
            const deletableItems = carrierConnectList.filter(canDeleteItem);
            selectAllRef.current.indeterminate =
            deletableItems.some(item => item.isChecked) &&
            !deletableItems.every(item => item.isChecked);
        }
    }, [carrierConnectList]);

    // Update data when API response comes back - similar to TrackHistory pattern
    // Compute total records from API response (handles various field names)
    const computeTotalRecordsFromResponse = (data) => {
        if (!data) return 0;
        if (data?.TotalRecords !== undefined) return data.TotalRecords || 0;
        return data?.totalRecords || data?.total || data?.TotalCount || data?.totalCount || 0;
    };

    const computeTotalPages = (totalRecords, pageSize) => {
        if (!totalRecords || totalRecords <= 0) return 1;
        const safePageSize = pageSize || 30; // NOSONAR
        return Math.ceil(totalRecords / safePageSize);
    };

    const updatePaginationIfNeeded = (newTotalRecords, newTotalPages) => {
        setPaginationData(prev => {
            if (prev.totalRecords !== newTotalRecords || prev.totalPages !== newTotalPages) {
                return { totalRecords: newTotalRecords, totalPages: newTotalPages };
            }
            return prev;
        });
    };

    const clearSearchBarIfNeeded = () => {
        // Clear search input after results are loaded
        // The search component will show lastSearchedKeyword instead of searchKeyword
        if (lastSearchedKeyword && filterData.searchKeyword) {
            setfilterData(prev => ({ ...prev, searchKeyword: '' }));
        }
    };

    useEffect(() => {
        if (carrierConnectData && carrierConnectStatus === 'success') {
            const newTotalRecords = computeTotalRecordsFromResponse(carrierConnectData);
            const newTotalPages = computeTotalPages(newTotalRecords, filterData.pageSize);

            updatePaginationIfNeeded(newTotalRecords, newTotalPages);
            clearSearchBarIfNeeded(); // Re-enabled for UI behavior
        }
    }, [carrierConnectData, carrierConnectStatus, filterData.pageSize]);

    // Initialize filterData from carrierConnectPrevState when returning from detail pages
    useEffect(() => {
        if (!_.isEmpty(carrierConnectPrevState)) {
            // Set ref immediately to prevent race conditions
            isRestoringFromPrevState.current = true;
            
            // Set flags to prevent race conditions
            setIsInitialLoad(false);
            setHasRestoredFromPrevState(true);
            
            // Set filter data
            setfilterData(carrierConnectPrevState);
            
            // If there was a search keyword, we need to trigger search
            if (carrierConnectPrevState.searchKeyword) {
                setLastSearchedKeyword(carrierConnectPrevState.searchKeyword);
                setShouldTriggerSearch(true);
            }
            
            // Call API directly with the restored state to avoid timing issues
            fetchCarrierConnectList(carrierConnectPrevState);
        } else {
            setfilterData({ ...DEFAULT_CARRIER_CONNECT_FILTERS });
        }
    }, []);

    

    const isPaginationChange = (fd) => (
        fd.pageIndex !== 1 || fd.pageSize !== 30 || 
        fd.sortColumn || 
        fd.sortOrder !== 'DESCENDING'
    );

    useEffect(() => {
        // Guard conditions first to reduce nesting
        if (_.isEmpty(filterData) || !filterData.pageIndex || !filterData.pageSize || isApiCallInProgress) return;
        
        // Don't trigger API calls if we're currently restoring from previous state
        // EXCEPT for pagination changes - allow pagination to work after restoration
        const isPaginationChangeFromRestored = hasRestoredFromPrevState && isPaginationChange(filterData);
        if ((isRestoringFromPrevState.current || hasRestoredFromPrevState) && !isPaginationChangeFromRestored) return;

        // Initial load should always fetch data
        if (isInitialLoad) {
            setIsInitialLoad(false);
            fetchCarrierConnectList();
            return;
        }

        // For pagination changes, always fetch data to maintain applied filters
        // For other filters, only fetch when explicitly triggered via shouldTriggerSearch
        if (shouldTriggerSearch || isPaginationChange(filterData) || lastSearchedKeyword) {
            const timeoutId = setTimeout(() => {
                if (!isApiCallInProgress) {
                    // Pass current filterData explicitly to avoid stale closure
                    fetchCarrierConnectList(filterData);
                    if (shouldTriggerSearch) setShouldTriggerSearch(false);
                }
            }, 150);

            return () => clearTimeout(timeoutId);
        }
    }, [filterData.pageSize, filterData.pageIndex, filterData.sortColumn, filterData.sortOrder, shouldTriggerSearch, isInitialLoad, hasRestoredFromPrevState, lastSearchedKeyword]);
                                                                                                                                                                                                                                                                                                                                                                                                                       
    const normalizeConsignmentStatusList = (list) => {
        if (!Array.isArray(list) || list.length === 0) return [];
        return list
            .map(item => {
                // Handle objects with value property (from dropdowns)
                if (item && typeof item === 'object' && item.value !== undefined) {
                    return item.value;
                }
                // Handle direct values
                return item;
            })
            .filter(item => typeof item === 'number' || (typeof item === 'string' && !isNaN(item))) // NOSONAR
            .map(item => (typeof item === 'string' ? parseInt(item) : item)); // NOSONAR
    };

    const buildApiDataFromFilters = (filters) => {// NOSONAR
        const safeFilters = filters || filterData;
        const safePageSize = safeFilters.pageSize || 30;
        const safePageIndex = safeFilters.pageIndex || 1;
        const companySellerId = userDetails?.Value?.isSuperAdmin
            ? 0
            : (userDetails?.Value?.CompanyId || userDetails?.Value?.CompanySellerId || 0);
        const consignmentStatusIdList = normalizeConsignmentStatusList(safeFilters.consignmentStatusIdList);

        // Precompute a few values to reduce inline conditional complexity in the returned object
        const sortOrder = safeFilters.sortOrder; // Send raw ASCENDING/DESCENDING like other modules
        const statusId = consignmentStatusIdList.length > 0 ? consignmentStatusIdList[0] : null;
        // Preserve time information for proper filtering - pass Date objects directly to service
        const fromDate = safeFilters.fromDate ? safeFilters.fromDate : null;
        const toDate = safeFilters.toDate ? safeFilters.toDate : null;
        const modifiedFromDate = safeFilters.modifiedFromDate ? safeFilters.modifiedFromDate : null;
        const modifiedToDate = safeFilters.modifiedToDate ? safeFilters.modifiedToDate : null;
        
        // Use lastSearchedKeyword for pagination to maintain search results across pages
        // But if searchKeyword is explicitly set (even to empty), use that instead
        // For reset operations, we want to ensure empty search keyword takes precedence
        const searchKeyword = safeFilters.searchKeyword || lastSearchedKeyword || '';

        const apiData = {
            pageIndex: safePageIndex,
            pageSize: safePageSize,
            sortColumn: safeFilters.sortColumn,
            sortOrder,
            carrierConnectNumber: safeFilters.carrierConnectId || '',
            trackingNumber: safeFilters.trackingNumber || '',
            statusId,
            fromDate,
            toDate,
            modifiedFromDate,
            modifiedToDate,
            companySellerId: companySellerId,
            searchKeyword: searchKeyword,
            carrierIdList: Array.isArray(safeFilters.carrierIdList) ? _.map(safeFilters.carrierIdList, item => item?.value || item) : [],
            companyIdList: Array.isArray(safeFilters.companyIdList) ? _.map(safeFilters.companyIdList, item => item?.Id || item) : [],
            sellerIdList: Array.isArray(safeFilters.sellerIdList) ? _.map(safeFilters.sellerIdList, item => item?.value || item) : [],
            billingStatusList: Array.isArray(safeFilters.billingStatusList) ? _.map(safeFilters.billingStatusList, item => item?.value || item) : [],
            consignmentStatusIdList: consignmentStatusIdList
        };
        
        return apiData;
    };

    const fetchCarrierConnectList = (appliedFilters = null) => {
        if (isApiCallInProgress) return;
        setIsApiCallInProgress(true);
        // Ensure we use the latest filterData if no specific filters provided
        const filtersToUse = appliedFilters || filterData;
        const apiData = buildApiDataFromFilters(filtersToUse);
        dispatch(getCarrierConnectListing(apiData)).finally(() => {
            setIsApiCallInProgress(false);
        });
    };

    

    const handleSortingClass = (columnName, isArrow = false) => {
        if (isArrow) {
            // Only show arrow if this column is currently selected for sorting
            if (filterData.sortColumn === columnName) {
                return filterData.sortOrder === "ASCENDING" ? "fa fa-arrow-up" : "fa fa-arrow-down";
            }
            // Show neutral arrow for non-selected columns
            return "fa fa-arrow-down";
        }
        return filterData.sortColumn === columnName ? 'active' : 'sortColumn'
    };

    const handleSortingColumn = (event, sortColumn) => {
        event.preventDefault();
        let newSortOrder;
        // If clicking on a different column, start with default sort order (DESCENDING)
        // If clicking on the same column, toggle the sort order
        if (filterData.sortColumn === sortColumn) {
            newSortOrder = filterData.sortOrder === "ASCENDING" ? "DESCENDING" : "ASCENDING";
        } else {
            newSortOrder = "DESCENDING"; // Default sort order for new columns
        }
        
        setfilterData({
            ...filterData,
            pageIndex: 1, // Reset to first page on sort change
            sortColumn: sortColumn,
            sortOrder: newSortOrder,
            searchKeyword: filterData.searchKeyword,
        });
    };

    // Toggle filter/search UI flags similar to other list components
    const handleFilterToggle = (key, value) => {
        setfilterData(prev => ({ ...prev, [key]: value }));
    };

    // Navigate to history page for an item (minimal implementation)
    const handleHistoryPage = (item) => {
        // Save state for history navigation too
        const stateToSave = {
            ...filterData,
            searchKeyword: filterData.searchKeyword || lastSearchedKeyword || ''
        };
        dispatch(carrierConnectBackDetails(stateToSave));
        
        const id = item?.id || item;
        navigate(`/carrier-connect/history-${id}`, {
            state: {
                data: item,
                editable: false,
                type: 'carrierConnect'
            }
        });
    };

    const handleNavigateToConnection = (item, type = '') => {
        // For state persistence, save the actual search term (either from filterData or lastSearchedKeyword)
        const stateToSave = {
            ...filterData,
            searchKeyword: filterData.searchKeyword || lastSearchedKeyword || ''
        };
        
        dispatch(carrierConnectBackDetails(stateToSave));
        if (type) {
            navigate(`/carrier-connect/${item}`, {
                state: {
                    data: item,
                    isEdit: type === 'edit' ? true : false,
                    searchState: stateToSave
                }
            });
            return;
        }
        navigate(`/carrier-connect/${item}`, {
            state: {
                data: item,
                isEdit: true,
                searchState: stateToSave
            }
        });
    };

    const setCurrentPagePagination = (value) => {
        const safePage = Math.max(1, parseInt(value) || 1); // NOSONAR
        
        // Reset restoration flags when pagination changes to allow API calls
        if (hasRestoredFromPrevState) {
            setHasRestoredFromPrevState(false);
            isRestoringFromPrevState.current = false;
        }
        
        setfilterData({ ...filterData, pageIndex: safePage });
    }

    const setTotalNumberOfPage = (value) => {
        const safePageSize = Math.max(1, parseInt(value) || 30); // NOSONAR
        
        // Reset restoration flags when page size changes to allow API calls
        if (hasRestoredFromPrevState) {
            setHasRestoredFromPrevState(false);
            isRestoringFromPrevState.current = false;
        }
        
        setfilterData({ ...filterData, pageSize: safePageSize, pageIndex: 1 });
    }

    const handleSearchText = (value) => {
        // Only update filterData if the value actually changed to avoid unnecessary re-renders
        if (filterData.searchKeyword !== value) {
            setfilterData({
                ...filterData,
                searchKeyword: value
            });
            // Don't clear lastSearchedKeyword here since we're not triggering search on input
        }
    };

    const handleSearchFilter = (e, type) => {
        if (type === "clear") {
            // Clear all filters and close the filter panel
            let clearedData = JSON.parse(JSON.stringify(dataLoad));
            clearedData.showFilter = false;
            clearedData.pageSize = filterData.pageSize;
            clearedData.pageIndex = 1;
            setfilterData(clearedData);
            setLastSearchedKeyword(''); // Clear the last searched keyword
            setShouldTriggerSearch(false); // Reset the search trigger
            setHasRestoredFromPrevState(false); // Allow normal API calls
            isRestoringFromPrevState.current = false; // Reset ref
            return;
        }
        if (type === "search") {
            // Handle search operation - reset pagination and capture the searched keyword
            setLastSearchedKeyword(filterData.searchKeyword || '');
            setShouldTriggerSearch(true); // Trigger the search
            setHasRestoredFromPrevState(false); // Allow normal API calls for new searches
            isRestoringFromPrevState.current = false; // Reset ref
            setfilterData({ ...filterData, pageIndex: 1 });
            return;
        }
        // Just reset pagination when applying filters
        setfilterData({ ...filterData, pageIndex: 1 });
    };

    const handleReset = () => {
        // Clear search-related states first
        setLastSearchedKeyword('');
        setShouldTriggerSearch(false);
        setHasRestoredFromPrevState(false);
        isRestoringFromPrevState.current = false;
        
        // Create reset filters with explicit empty search
        const resetFilters = {
            ...filterData,
            searchKeyword: '',
            pageIndex: 1
        };
        
        // Update state 
        setfilterData(resetFilters);
        
        // Immediately trigger API call with reset filters to get unfiltered results
        setTimeout(() => {
            if (!isApiCallInProgress) {
                const apiData = buildApiDataFromFilters(resetFilters);
                // Explicitly ensure search keyword is empty
                apiData.searchKeyword = '';
                
                setIsApiCallInProgress(true);
                dispatch(getCarrierConnectListing(apiData)).finally(() => {
                    setIsApiCallInProgress(false);
                });
            }
        }, 100);
    };

    // Compute visible table column count - company/seller column is only visible to super admins
    // Checkbox column is only visible when user can see delete UI
    const baseColumnCount = userDetails?.Value?.isSuperAdmin ? 10 : 9;
    const tableColumnCount = canSeeDeleteUI ? baseColumnCount : baseColumnCount - 1;

    const handleCheckBox = (event, type = '') => {
        const { checked } = event.target;
        if (type === 'all') {
            // Only toggle items the current user is allowed to delete
            const allList = carrierConnectList.map(item =>
                canDeleteItem(item) ? { ...item, isChecked: checked } : item
            );
            setCarrierConnectList(allList);
            return;
        } else {
            const id = Number(event.target.getAttribute('data-id')); // NOSONAR
            const allList = carrierConnectList.map(item => item.id === id ? { ...item, isChecked: checked } : item);
            setCarrierConnectList(allList);
        }
    };

    const handleDeleteRecords = () => {
        setShowDeleteModal(true);
        setIsApiCallInProgress(false);
    };

    const handleConfirmDelete = async () => {
        let payload = {
            carrierConnectIds: _.map(_.filter(carrierConnectList, (item) => item.isChecked), 'id')
        }
        let deleteResponse = await deleteCarrierConnectRecords(payload);
        if (deleteResponse?.status === 200) {
            const successCount = deleteResponse?.data?.Entity?.successCount;
            const failedCount = deleteResponse?.data?.Entity?.failedCount;
            let message = 'Record(s) Deleted Successfully';
            if (successCount !== undefined) {
                message = `${successCount} Record(s) Deleted Successfully`;
                if (failedCount) {
                    message += `, ${failedCount} failed`;
                }
            }
            toast.success(message);
            setShowDeleteModal(false);
            fetchCarrierConnectList(filterData);
        } else {
            toast.error('Failed to delete records. Please try again.');
            setShowDeleteModal(false);
        }
    };

    return (
        <main id="CarrierConnect">
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>Carrier Connect</span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            {canSeeDeleteUI && (
                                <button
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={handleDeleteRecords}
                                    disabled={!carrierConnectList.some(item => item.isChecked)}
                                >Delete Selected</button>
                            )}

                            {checkPermission(FEATURE_CARRIERCONNECTMANAGEMENT, '', permssionCreate, featureModule, props.employeePermissions) && !isWarehouseRole() && (
                                <button
                                    className="btn cus-seconday-bg-btn top-btn"
                                    onClick={() => handleNavigateToConnection(0)}
                                >Add New</button>
                            )}
                            <Button
                                className="cus-seconday-bg-btn top-btn addNewProduct"
                                onClick={() => handleFilterToggle('showFilter', !filterData.showFilter)}
                            >Filter</Button>
                        </div>
                    </div>
                </div>

                {filterData.showFilter && (
                    <CarrierConnectFilter
                        showFilter={filterData.showFilter}
                        handleReset={handleSearchFilter}
                        fetchCarrierConnectList={(uiData) => {
                            // Update filter data with UI-friendly data (preserves dropdown selections)
                            if (uiData) {
                                setfilterData(uiData);
                            }
                            setShouldTriggerSearch(true);
                        }}
                        filterData={filterData}
                        setfilterData={setfilterData}
                        handleDateFieldChange={(date, field) => {
                            setfilterData({ ...filterData, [field]: date });
                        }}
                    />
                )}

                <SearchCarrierConnect
                    searchKeyword={filterData.searchKeyword}
                    handleSearchText={handleSearchText}
                    handleSearchFilter={handleSearchFilter}
                    handleReset={handleReset}
                    userDetails={userDetails}
                    lastSearchedKeyword={lastSearchedKeyword}
                />

                <div className="main_content cus-page-content-sec srch-pt0">
                    {carrierConnectStatus !== "success" && <LoaderSpinner />}
                    <div className="main_content_table_history">
                        <div className='tableFixHead incomingTbl'>
                            <Table className={`tableWide table-height cus-left-right-col-layout`}>
                                <thead className="tbl-btch">
                                    <tr className="table-rw-batch">
                                        {/* Carrier Connect ID column - visible to all roles */}
                                        {canSeeDeleteUI && (
                                            <th className="wrapCell">
                                                <input 
                                                    type="checkbox" 
                                                    onChange={(e) => handleCheckBox(e, 'all')}
                                                    checked={carrierConnectList.filter(canDeleteItem).length > 0 && carrierConnectList.filter(canDeleteItem).every(item => item.isChecked)}
                                                    ref={selectAllRef}
                                                />
                                            </th>
                                        )}
                                        <th className="wrapCell">
                                            <button
                                                className={handleSortingClass('carrierConnectNumber')}
                                                onClick={(e) => handleSortingColumn(e, "carrierConnectNumber")}
                                                id="carrierConnectNumber"
                                                aria-label="Sort by Carrier Connect ID"
                                                style={{ 
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                CarrierConnectId <i className={handleSortingClass('carrierConnectNumber', true)} aria-hidden="true"></i>
                                            </button>
                                        </th>
                                        {userDetails?.Value?.isSuperAdmin && (
                                            <th className="company-seller-name-column">
                                                <button
                                                    className={handleSortingClass('companySellerName')}
                                                    onClick={(e) => handleSortingColumn(e, "companySellerName")}
                                                    id="companySellerName"
                                                    aria-label="Sort by Company / Seller Name"
                                                    style={{ 
                                                        textAlign: 'center', 
                                                        cursor: 'pointer',
                                                        background: 'none',
                                                        border: 'none',
                                                        padding: 0,
                                                        width: '100%',
                                                        font: 'inherit'
                                                    }}>
                                                    Company / Seller Name <i className={handleSortingClass('companySellerName', true)} aria-hidden="true"></i>
                                                </button>
                                            </th>
                                        )}
                                        <th>
                                            <button
                                                className={handleSortingClass('carrierName')}
                                                onClick={(e) => handleSortingColumn(e, "carrierName")}
                                                id="carrierName"
                                                aria-label="Sort by Carrier"
                                                style={{ 
                                                    textAlign: 'center', 
                                                    maxWidth: '100px', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Carrier <i className={handleSortingClass('carrierName', true)}  aria-hidden="true"></i>
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                className={handleSortingClass('packageCount')}
                                                onClick={(e) => handleSortingColumn(e, "packageCount")}
                                                id="packageCount"
                                                aria-label="Sort by Package Count"
                                                style={{ 
                                                    textAlign: 'center', 
                                                    maxWidth: '120px', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Package Count <i className={handleSortingClass('packageCount', true)} aria-hidden="true"></i>
                                            </button>
                                        </th>
                                        <th className="wrapCell">
                                            <button
                                                className={handleSortingClass('trackingNumber')}
                                                onClick={(e) => handleSortingColumn(e, "trackingNumber")}
                                                id="trackingNumber"
                                                aria-label="Sort by Tracking Number"
                                                style={{
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Tracking Number <i className={handleSortingClass('trackingNumber', true)} aria-hidden="true"></i>
                                            </button>
                                        </th>
                                        <th className="wrapCell">
                                            <button
                                                className={handleSortingClass('subTrackingNumber')}
                                                onClick={(e) => handleSortingColumn(e, "subTrackingNumber")}
                                                id="subTrackingNumber"
                                                aria-label="Sort by Sub-Tracking Number"
                                                style={{ 
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Sub-Tracking Number <i className={handleSortingClass('subTrackingNumber', true)} aria-hidden="true"></i>
                                            </button>
                                        </th>

                                        <th width={'5%'}>
                                            <button
                                                className={handleSortingClass('statusName')}
                                                onClick={(e) => handleSortingColumn(e, "statusName")}
                                                id="statusName"
                                                aria-label="Sort by Status"
                                                style={{
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Status <i className={handleSortingClass('statusName', true)} aria-hidden="true"></i>
                                            </button>
                                        </th>
                                        <th width={'5%'}>
                                            <button
                                                className={handleSortingClass('billingStatus')}
                                                onClick={(e) => handleSortingColumn(e, "billingStatus")}
                                                id="billingStatus"
                                                aria-label="Sort by Billing Status"
                                                style={{
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Billing Status <i className={handleSortingClass('billingStatus', true)} aria-hidden="true"></i>
                                            </button>
                                        </th>
                                        <th>
                                            <button
                                                className={handleSortingClass('createdDate')}
                                                onClick={(e) => handleSortingColumn(e, "createdDate")}
                                                id="createdDate"
                                                aria-label="Sort by Created Date"
                                                style={{
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Created Date <i className={handleSortingClass('createdDate', true)} aria-hidden="true"></i>
                                            </button>

                                        </th>
                                        <th>
                                            <button
                                                className={handleSortingClass('modifiedDate')}
                                                onClick={(e) => handleSortingColumn(e, "modifiedDate")}
                                                id="modifiedDate"
                                                aria-label="Sort by Modified Date"
                                                style={{
                                                    textAlign: 'center', 
                                                    cursor: 'pointer',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    width: '100%',
                                                    font: 'inherit'
                                                }}>
                                                Modified Date <i className={handleSortingClass('modifiedDate', true)} aria-hidden="true"></i>
                                            </button>

                                        </th>
                                        <th className="sticky" style={{textAlign: 'center'}}>Action</th>
                                    </tr>
                                </thead>
                                {console.log('carrierConnectList-------->',carrierConnectList)}
                                <tbody className="tbl-bt-body">
                                    {carrierConnectList && carrierConnectList.length > 0 ?
                                        carrierConnectList.map((item) => (
                                            <tr key={item.id}>
                                                {/* CarrierConnectId mapping: prefer carrierConnectId, fallback to carrierConnectNumber or id */}
                                                {canSeeDeleteUI && (
                                                    <td className="wrapCell">
                                                        {canDeleteItem(item) ? (
                                                            <input checked={item.isChecked} type="checkbox" onChange={(e) => handleCheckBox(e)} data-id={item.id} />
                                                        ) : null}
                                                    </td>
                                                )}
                                                <td className="wrapCell" style={{textAlign: 'center'}}>{item.carrierConnectNumber || item.id}</td>
                                                {userDetails?.Value?.isSuperAdmin && (
                                                    <td className="company-seller-name-column">{item.companySellerName}</td>
                                                )}
                                                <td style={{textAlign: 'center'}}>{item.carrierName}</td>
                                                <td style={{textAlign: 'center'}}>{item.packageCount || 0}</td>
                                                <td className="wrapCell" style={{textAlign: 'center'}}>{item.trackingNumber}</td>
                                                <td className="wrapCell" style={{textAlign: 'center'}}>{item.subTrackingNumber || '-'}</td>
                                                <td>
                                                    <span className='statusTd'>
                                                        <span className={getStatusClass(item.statusName)}>{item.statusName}</span>
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className='statusTd'>
                                                        <span className={''}>{item.billingStatus}</span>
                                                    </span>
                                                </td>
                                                <td>{moment(item.createdDate).format("MM-DD-YYYY | h:mm:ss A")}</td>
                                                <td>{item.modifiedDate ? moment(item.modifiedDate).format("MM-DD-YYYY | h:mm:ss A") : '-'}</td>
                                                <td className="sticky" style={{textAlign: 'center'}}>
                                                    {checkPermission(FEATURE_CARRIERCONNECTMANAGEMENT, '', permssionView, featureModule, props.employeePermissions) && (
                                                        <ActionItems type={'View'} item={item} handleAction={() => handleNavigateToConnection(item.id, 'view')} />
                                                    )}
                                                    {checkPermission(FEATURE_CARRIERCONNECTMANAGEMENT, '', permssionEdit, featureModule, props.employeePermissions) && (
                                                        <ActionItems type={'Edit'} item={item} handleAction={() => handleNavigateToConnection(item.id, 'edit')} />
                                                    )}
                                                    <ActionItems type={'History'} item={item} handleAction={() => handleHistoryPage(item)} />
                                                </td>
                                            </tr>
                                        ))
                                        : null
                                    }

                                    {carrierConnectStatus === 'success' && carrierConnectList.length === 0 && (
                                        <>{renderTableRow(tableColumnCount, "No Result Found...")}</>
                                    )}

                                    {carrierConnectStatus === 'failure' && (
                                        <>{renderTableRow(tableColumnCount, "Failed to load data. Please try again.")}</>
                                    )}

                                    {(!carrierConnectData && carrierConnectStatus === 'idle') && (
                                        <>{renderTableRow(tableColumnCount, "Loading...")}</>
                                    )}
                                </tbody>
                            </Table >
                        </div>
                    </div>
                    <BatchPagination
                        gridData={carrierConnectList}
                        totalRecords={paginationData.totalRecords || 0}
                        numberOfPage={filterData.pageSize}
                        numOfPage={numOfPage}
                        loader={carrierConnectStatus === 'pending'}
                        currentPage={filterData.pageIndex}
                        totalPages={paginationData.totalPages || 1}
                        setCurrentPage={setCurrentPagePagination}
                        setNumberOfPage={setTotalNumberOfPage}
                    />
                </div>

                <AlertDialogModal
                    modalClass={'modalDeleteWrapper'}
                    modalShow={showDeleteModal}
                    handleCancelDeleteAlert={() => setShowDeleteModal(false)}
                    handleYesDeleteAlert={handleConfirmDelete}
                    messageText={'Are you sure you want to delete the selected Carrier Connect records? This action cannot be undone.'}
                    headingText={'Delete'}
                    loader={false}
                />
            </div>
        </main >
    );
}

CarrierConnectComponent.propTypes = {
    employeePermissions: PropTypes.array
};

export default CarrierConnectComponent;
